import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  DeleteOutlined,
  LineChartOutlined,
  LockOutlined,
  PlusOutlined,
  ProfileOutlined,
  SaveOutlined,
  TableOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { ConfigProvider } from 'antd';

import { Bento, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Button, Empty, Field, Input, Skeleton } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { ChonIcon, ChonMauNhan, ChonXuHuong } from '../components/ChonEnum';
import { ChonNguon } from '../components/ChonNguon';
import { DaiBao } from '../components/DaiBao';
import { XuHuong } from '../components/XuHuong';
import { KHOA_LOI_PHAN_HE, QUYEN, QUYEN_PHAN_HE, SLUG_TO_MODULE } from '../constants';
import { laChuaNhap, useLoiBaoCao, useLuuPhanHe, usePhanHe } from '../hooks/useBaoCao';
import { useNguon } from '../hooks/useNguon';
import {
  banNhapMoi,
  heroMoi,
  kiemBanNhap,
  maChiSoMoi,
  maDongMoi,
  thanPut,
  tuPayload,
} from '../nghiep-vu/payload';
import type { BanNhapPhanHe, StatNhap } from '../nghiep-vu/payload';
import type { SlugPhanHe } from '../types';

const TONE = 'warning';

/** Bản nháp đang sửa, gắn với **đúng** phân hệ và nguồn nó mô tả. */
interface Nhap {
  khoa: string;
  b: BanNhapPhanHe;
}

/**
 * Số liệu một phân hệ (`/bao-cao-doanh-nghiep/{slug}`) mà app di động hiển thị: thẻ chủ đạo, ô chỉ số, bảng
 * xếp hạng. Một bản nháp cho cả trang — PUT ghi đè trọn gói, khối vắng là xoá khối, nên mọi khối là khung
 * nhìn của **một** bản khai.
 *
 * Route chặn theo phân hệ `BIZ_*` (theo dịch vụ); màn tự kiểm `bao-cao:{slug}:read` — thiếu thì nói thẳng
 * thay vì gọi API rồi nhận `403`. `404` không phải hỏng: phân hệ chưa có số liệu, màn cho nhập mới.
 *
 * ⚠️ Bản nháp **suy ra lúc vẽ** từ khoá `phân hệ|nguồn`: đổi nguồn là bản nháp cũ hết hiệu lực ngay ở lần vẽ
 * đó, không đợi effect. Giữ bản nháp qua lần đổi nguồn thì bấm Lưu sẽ chép số liệu nguồn cũ sang nguồn mới.
 */
export function PhanHeView({ slug }: { slug: SlugPhanHe }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const quyen = useModulePermissions(QUYEN);
  const duocDoc = quyen[QUYEN_PHAN_HE[slug].doc];
  const duocSua = quyen[QUYEN_PHAN_HE[slug].sua];

  const moduleId = SLUG_TO_MODULE[slug];
  const tenPhanHe = t(`baoCaoDoanhNghiep.phanHe.${moduleId}`);
  const nguon = useNguon();
  const q = usePhanHe(moduleId, nguon.code, nguon.sanSang, duocDoc);
  const luu = useLuuPhanHe(moduleId, nguon.code);

  const khoa = `${moduleId}|${nguon.code ?? ''}`;
  const [nhap, setNhap] = useState<Nhap | null>(null);
  const [daGui, setDaGui] = useState<string | null>(null);
  const [loiLuu, setLoiLuu] = useState<{ khoa: string; loi: unknown } | null>(null);
  // Đổi nguồn hay phân hệ: bỏ bản nháp ngay trong lần vẽ này (mẫu "điều chỉnh state khi prop đổi" của
  // React), không qua effect — một nhịp vẽ với bản nháp cũ là một nhịp nút Lưu ghi nhầm nguồn.
  if (nhap !== null && nhap.khoa !== khoa) setNhap(null);

  const chuaNhap = laChuaNhap(q.error);
  const goc: BanNhapPhanHe | null = q.data ? tuPayload(q.data) : chuaNhap ? banNhapMoi(tenPhanHe) : null;
  const dangSua = nhap !== null && nhap.khoa === khoa;
  const b = dangSua ? nhap.b : goc;
  const ghiDuoc = duocSua && nguon.ghiDuoc;

  const header = (actions?: ReactNode) => (
    <PageHeader
      breadcrumb={[{ label: t('nav.nhom.baoCaoDoanhNghiep') }, { label: t('nav.muc.phanHe') }]}
      titleIcon={<TableOutlined className="text-warning-600" />}
      title={tenPhanHe}
      subtitle={t('baoCaoDoanhNghiep.phanHeMoTa')}
      actions={actions}
    />
  );

  if (!duocDoc) {
    return (
      <Khung>
        {header()}
        <Tile>
          <Empty
            icon={LockOutlined}
            title={t('baoCaoDoanhNghiep.khongCoQuyen.tieuDe')}
            description={t('baoCaoDoanhNghiep.khongCoQuyen.moTa', { quyen: QUYEN[QUYEN_PHAN_HE[slug].doc] })}
          />
        </Tile>
      </Khung>
    );
  }

  const chonNguon = <ChonNguon nguon={nguon} />;

  // Lỗi xét trước "đang tải": `b` chỉ có khi truy vấn thành công hoặc `404`, đảo hai nhánh là kẹt ở khung xương.
  if (q.isError && !chuaNhap) {
    return (
      <Khung>
        {header(chonNguon)}
        <DaiBao
          tong="loi"
          hanhDong={
            <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
              {t('chung.thuLai')}
            </Button>
          }
        >
          {t('baoCaoDoanhNghiep.loiTaiPhanHe', { loi: dichLoi(q.error) })}
        </DaiBao>
      </Khung>
    );
  }

  if (b === null) {
    return (
      <Khung>
        {header(chonNguon)}
        <Bento aria-busy="true">
          {['lg:col-span-4', 'lg:col-span-8', 'lg:col-span-7', 'lg:col-span-5'].map((span) => (
            <Tile key={span} span={span}>
              <div className="space-y-3 p-5">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            </Tile>
          ))}
        </Bento>
      </Khung>
    );
  }

  const doi = (p: Partial<BanNhapPhanHe>) => setNhap({ khoa, b: { ...b, ...p } });
  const doiStat = (i: number, p: Partial<StatNhap>) => doi({ stats: b.stats.map((s, j) => (j === i ? { ...s, ...p } : s)) });

  const loiMay = daGui === khoa ? kiemBanNhap(b) : {};
  const loiServer = loiLuu?.khoa === khoa ? loiTheoTruong(loiLuu.loi) : {};
  const loiO = (duong: string): string | undefined => {
    const ma = loiMay[duong];
    if (ma) return t(KHOA_LOI_PHAN_HE[ma]);
    return loiServer[`/${duong.replace(/\./g, '/')}`];
  };
  const soLoi = Object.keys(loiMay).length;
  const loiChung = loiLuu?.khoa === khoa && Object.keys(loiServer).length === 0 ? dichLoi(loiLuu.loi) : null;

  const onLuu = () => {
    setDaGui(khoa);
    setLoiLuu(null);
    if (Object.keys(kiemBanNhap(b)).length > 0) return;
    const khoaLuc = khoa;
    luu.mutate(thanPut(b), {
      onSuccess: () => {
        // Vẽ lại từ phản hồi của server (đã vào cache): trường server bỏ đi phải biến mất ngay trước mắt.
        setNhap((cu) => (cu?.khoa === khoaLuc ? null : cu));
        setDaGui(null);
        toast.success(t('baoCaoDoanhNghiep.daLuuPhanHe', { ten: tenPhanHe }));
      },
      onError: (loi) => setLoiLuu({ khoa: khoaLuc, loi }),
    });
  };

  const huyThayDoi = () => {
    setNhap(null);
    setDaGui(null);
    setLoiLuu(null);
  };

  return (
    <Khung>
      {header(
        <>
          {chonNguon}
          {ghiDuoc && dangSua && (
            <Button variant="outline" size="md" onClick={huyThayDoi} disabled={luu.isPending}>
              {t('baoCaoDoanhNghiep.boThayDoi')}
            </Button>
          )}
          {ghiDuoc && (
            <Button variant="primary" size="md" icon={<SaveOutlined />} loading={luu.isPending} onClick={onLuu}>
              {luu.isPending ? t('chung.dangLuu') : t('baoCaoDoanhNghiep.luuSoLieu')}
            </Button>
          )}
        </>,
      )}

      {nguon.nguon && !nguon.nguon.isActive && (
        <DaiBao tong="canhBao">{t('baoCaoDoanhNghiep.nguonDaNgungBanner', { ten: nguon.nguon.name })}</DaiBao>
      )}
      {!duocSua && <DaiBao tong="thongTin">{t('baoCaoDoanhNghiep.chiXem')}</DaiBao>}
      {chuaNhap && !dangSua && (
        <DaiBao tong="thongTin">
          {t(ghiDuoc ? 'baoCaoDoanhNghiep.chuaNhap' : 'baoCaoDoanhNghiep.chuaNhapChiXem', { ten: tenPhanHe })}
        </DaiBao>
      )}
      {soLoi > 0 && <DaiBao tong="loi">{t('baoCaoDoanhNghiep.conLoi', { so: soVN(soLoi, locale) })}</DaiBao>}
      {loiChung && <DaiBao tong="loi">{t('baoCaoDoanhNghiep.khongLuuDuoc', { loi: loiChung })}</DaiBao>}

      {/* Không ghi được (thiếu quyền sửa, nguồn đã ngừng) thì mọi ô chỉ đọc. */}
      <ConfigProvider componentDisabled={!ghiDuoc || luu.isPending}>
        <Bento>
          <Tile span="lg:col-span-4">
            <TileHeader icon={<ProfileOutlined />} tone={TONE} title={t('baoCaoDoanhNghiep.thongTinChung')} />
            <TileBody className="space-y-3">
              <Field label={t('baoCaoDoanhNghiep.tieuDePhanHe')} required error={loiO('title')}>
                {(p) => <Input {...p} value={b.title} maxLength={120} onChange={(e) => doi({ title: e.target.value })} />}
              </Field>
              <Field label={t('baoCaoDoanhNghiep.tieuDeKhoiTaiLieu')} hint={t('baoCaoDoanhNghiep.tieuDeKhoiTaiLieuGoiY')}>
                {(p) => (
                  <Input {...p} value={b.documentsTitle} maxLength={120} onChange={(e) => doi({ documentsTitle: e.target.value })} />
                )}
              </Field>
              {q.data && (
                <dl className="space-y-1 border-t border-default pt-3 text-xs">
                  <div className="flex flex-wrap gap-x-1.5">
                    <dt className="text-content-muted">{t('baoCaoDoanhNghiep.soTaiLieuPhanHe')}</dt>
                    <dd className="tabular-nums text-content-secondary">{soVN(q.data.documentsTotal ?? 0, locale)}</dd>
                  </div>
                  {q.data.updatedAt && (
                    <div className="flex flex-wrap gap-x-1.5">
                      <dt className="text-content-muted">{t('baoCaoDoanhNghiep.capNhatLuc')}</dt>
                      <dd className="tabular-nums text-content-secondary">{ngayGioVN(q.data.updatedAt, locale)}</dd>
                    </div>
                  )}
                </dl>
              )}
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-8">
            <TileHeader
              icon={<LineChartOutlined />}
              tone={TONE}
              title={t('baoCaoDoanhNghiep.hero.tieuDe')}
              mo={t('baoCaoDoanhNghiep.ghiChuGiaTri')}
              action={
                ghiDuoc &&
                (b.hero === null ? (
                  <Button variant="ghost" size="sm" icon={<PlusOutlined />} onClick={() => doi({ hero: heroMoi() })}>
                    {t('baoCaoDoanhNghiep.hero.them')}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<DeleteOutlined />}
                    title={t('baoCaoDoanhNghiep.hero.go')}
                    aria-label={t('baoCaoDoanhNghiep.hero.go')}
                    onClick={() => doi({ hero: null })}
                  />
                ))
              }
            />
            <TileBody>
              {b.hero === null ? (
                <p className="py-6 text-center text-sm text-content-muted">{t('baoCaoDoanhNghiep.hero.trong')}</p>
              ) : (
                <HeroForm b={b} doi={doi} loiO={loiO} />
              )}
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-7">
            <TileHeader
              icon={<TableOutlined />}
              tone={TONE}
              title={t('baoCaoDoanhNghiep.chiSo.tieuDe')}
              action={
                ghiDuoc && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      doi({
                        stats: [...b.stats, { id: maChiSoMoi(b.stats), label: '', value: '', delta: '', trend: 'flat',
                          accentName: 'primary', iconName: 'chart-column' }],
                      })
                    }
                  >
                    {t('baoCaoDoanhNghiep.chiSo.them')}
                  </Button>
                )
              }
            />
            <TileBody className="space-y-3">
              {b.stats.length === 0 ? (
                <p className="py-6 text-center text-sm text-content-muted">{t('baoCaoDoanhNghiep.chiSo.trong')}</p>
              ) : (
                b.stats.map((s, i) => (
                  <div key={i} className="rounded border border-default p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium text-content-primary">
                          {s.label || t('baoCaoDoanhNghiep.chiSo.chuaDatTen')}
                        </span>
                        <XuHuong trend={s.trend} delta={s.delta} />
                      </span>
                      {ghiDuoc && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<DeleteOutlined />}
                          title={t('baoCaoDoanhNghiep.chiSo.go', { ten: s.label || s.id })}
                          aria-label={t('baoCaoDoanhNghiep.chiSo.go', { ten: s.label || s.id })}
                          onClick={() => doi({ stats: b.stats.filter((_, j) => j !== i) })}
                        />
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <Field label={t('baoCaoDoanhNghiep.chiSo.ma')} required error={loiO(`stats.${i}.id`)}>
                        {(p) => <Input {...p} className="font-mono" value={s.id} maxLength={64} onChange={(e) => doiStat(i, { id: e.target.value })} />}
                      </Field>
                      <Field label={t('baoCaoDoanhNghiep.chiSo.nhan')} required error={loiO(`stats.${i}.label`)}>
                        {(p) => <Input {...p} value={s.label} maxLength={120} onChange={(e) => doiStat(i, { label: e.target.value })} />}
                      </Field>
                      <Field label={t('baoCaoDoanhNghiep.chiSo.giaTri')} required error={loiO(`stats.${i}.value`)}>
                        {(p) => <Input {...p} value={s.value} maxLength={60} onChange={(e) => doiStat(i, { value: e.target.value })} />}
                      </Field>
                      <Field label={t('baoCaoDoanhNghiep.hero.delta')}>
                        {(p) => <Input {...p} value={s.delta} maxLength={30} onChange={(e) => doiStat(i, { delta: e.target.value })} />}
                      </Field>
                      <Field label={t('baoCaoDoanhNghiep.hero.xuHuong')}>
                        {(p) => <ChonXuHuong {...p} value={s.trend} onChange={(v) => doiStat(i, { trend: v })} />}
                      </Field>
                      <Field label={t('baoCaoDoanhNghiep.chiSo.bieuTuong')}>
                        {(p) => <ChonIcon {...p} value={s.iconName} onChange={(v) => doiStat(i, { iconName: v })} />}
                      </Field>
                      <Field label={t('baoCaoDoanhNghiep.chiSo.mauNhan')}>
                        {(p) => <ChonMauNhan {...p} value={s.accentName} onChange={(v) => doiStat(i, { accentName: v })} />}
                      </Field>
                    </div>
                  </div>
                ))
              )}
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-5">
            <TileHeader
              icon={<TrophyOutlined />}
              tone={TONE}
              title={t('baoCaoDoanhNghiep.xepHang.tieuDe')}
              action={
                ghiDuoc &&
                (b.rank === null ? (
                  <Button variant="ghost" size="sm" icon={<PlusOutlined />} onClick={() => doi({ rank: { title: '', rows: [] } })}>
                    {t('baoCaoDoanhNghiep.xepHang.them')}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<DeleteOutlined />}
                    title={t('baoCaoDoanhNghiep.xepHang.go')}
                    aria-label={t('baoCaoDoanhNghiep.xepHang.go')}
                    onClick={() => doi({ rank: null })}
                  />
                ))
              }
            />
            <TileBody className="space-y-3">
              {b.rank === null ? (
                <p className="py-6 text-center text-sm text-content-muted">{t('baoCaoDoanhNghiep.xepHang.trong')}</p>
              ) : (
                <BangXepHang b={b} doi={doi} loiO={loiO} ghiDuoc={ghiDuoc} />
              )}
            </TileBody>
          </Tile>
        </Bento>
      </ConfigProvider>
    </Khung>
  );
}

function Khung({ children }: { children: ReactNode }) {
  return <div className="space-y-4 p-4 lg:p-6">{children}</div>;
}

function HeroForm({
  b,
  doi,
  loiO,
}: {
  b: BanNhapPhanHe;
  doi: (p: Partial<BanNhapPhanHe>) => void;
  loiO: (duong: string) => string | undefined;
}) {
  const { t } = useT();
  const hero = b.hero!;
  const doiHero = (p: Partial<typeof hero>) => doi({ hero: { ...hero, ...p } });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label={t('baoCaoDoanhNghiep.hero.nhan')} required error={loiO('hero.label')}>
        {(p) => <Input {...p} value={hero.label} maxLength={120} onChange={(e) => doiHero({ label: e.target.value })} />}
      </Field>
      {/* Chữ, không `type="number"`: giá trị là chuỗi đã định dạng (`8,42 tỷ`), ô số chặn đúng dạng đó. */}
      <Field label={t('baoCaoDoanhNghiep.hero.giaTri')} required error={loiO('hero.value')}>
        {(p) => <Input {...p} value={hero.value} maxLength={60} onChange={(e) => doiHero({ value: e.target.value })} />}
      </Field>
      <Field label={t('baoCaoDoanhNghiep.hero.delta')}>
        {(p) => <Input {...p} value={hero.delta} maxLength={30} onChange={(e) => doiHero({ delta: e.target.value })} />}
      </Field>
      <Field label={t('baoCaoDoanhNghiep.hero.xuHuong')} hint={t('baoCaoDoanhNghiep.ghiChuXuHuong')}>
        {(p) => <ChonXuHuong {...p} value={hero.trend} onChange={(v) => doiHero({ trend: v })} />}
      </Field>
      <Field label={t('baoCaoDoanhNghiep.hero.chuThich')} className="sm:col-span-2">
        {(p) => <Input {...p} value={hero.caption} maxLength={200} onChange={(e) => doiHero({ caption: e.target.value })} />}
      </Field>
      <Field
        label={t('baoCaoDoanhNghiep.hero.spark')}
        required
        hint={t('baoCaoDoanhNghiep.ghiChuSpark')}
        error={loiO('hero.spark')}
        className="sm:col-span-2"
      >
        {(p) => (
          <Input
            {...p}
            className="font-mono"
            inputMode="decimal"
            placeholder={t('baoCaoDoanhNghiep.hero.sparkGoiY')}
            value={hero.sparkChu}
            onChange={(e) => doiHero({ sparkChu: e.target.value })}
          />
        )}
      </Field>
    </div>
  );
}

function BangXepHang({
  b,
  doi,
  loiO,
  ghiDuoc,
}: {
  b: BanNhapPhanHe;
  doi: (p: Partial<BanNhapPhanHe>) => void;
  loiO: (duong: string) => string | undefined;
  ghiDuoc: boolean;
}) {
  const { t } = useT();
  const rank = b.rank!;
  const doiDong = (i: number, p: { label?: string; value?: string }) =>
    doi({ rank: { ...rank, rows: rank.rows.map((r, j) => (j === i ? { ...r, ...p } : r)) } });

  return (
    <>
      <Field label={t('baoCaoDoanhNghiep.xepHang.tenBang')} required error={loiO('rank.title')}>
        {(p) => <Input {...p} value={rank.title} maxLength={120} onChange={(e) => doi({ rank: { ...rank, title: e.target.value } })} />}
      </Field>
      {rank.rows.length === 0 && <p className="text-xs text-content-muted">{t('baoCaoDoanhNghiep.xepHang.trongDong')}</p>}
      <ol className="space-y-2">
        {rank.rows.map((r, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-8 w-5 shrink-0 text-right text-xs tabular-nums text-content-muted">{i + 1}</span>
            <Field label={t('baoCaoDoanhNghiep.chiSo.nhan')} required error={loiO(`rank.rows.${i}.label`)} className="min-w-0 flex-1">
              {(p) => <Input {...p} value={r.label} maxLength={120} onChange={(e) => doiDong(i, { label: e.target.value })} />}
            </Field>
            <Field label={t('baoCaoDoanhNghiep.chiSo.giaTri')} required error={loiO(`rank.rows.${i}.value`)} className="w-28 shrink-0">
              {(p) => <Input {...p} value={r.value} maxLength={60} onChange={(e) => doiDong(i, { value: e.target.value })} />}
            </Field>
            {ghiDuoc && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-7 shrink-0"
                icon={<DeleteOutlined />}
                title={t('baoCaoDoanhNghiep.xepHang.goDong', { ten: r.label || String(i + 1) })}
                aria-label={t('baoCaoDoanhNghiep.xepHang.goDong', { ten: r.label || String(i + 1) })}
                onClick={() => doi({ rank: { ...rank, rows: rank.rows.filter((_, j) => j !== i) } })}
              />
            )}
          </li>
        ))}
      </ol>
      {ghiDuoc && (
        <Button
          variant="outline"
          size="sm"
          icon={<PlusOutlined />}
          onClick={() => doi({ rank: { ...rank, rows: [...rank.rows, { id: maDongMoi(rank.rows), label: '', value: '' }] } })}
        >
          {t('baoCaoDoanhNghiep.xepHang.themDong')}
        </Button>
      )}
    </>
  );
}
